import type { GuidedTourPosition, GuidedTourStep } from '../components/guidedTour/types';

export function scrollTourTargetIntoView(
  element: HTMLElement,
  block: ScrollLogicalPosition = 'center'
): void {
  try {
    element.scrollIntoView({ behavior: 'smooth', block, inline: 'nearest' });
  } catch {
    try {
      element.scrollIntoView(true);
    } catch {
      /* ignore */
    }
  }
}

/** Resolves after smooth scroll settles (or timeout if no scroll occurs). */
export function waitForScrollEnd(timeoutMs = 450): Promise<void> {
  return new Promise((resolve) => {
    let settled = false;

    const finish = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      document.removeEventListener('scrollend', onScrollEnd, true);
      resolve();
    };

    const onScrollEnd = () => {
      window.setTimeout(finish, 32);
    };

    const timer = window.setTimeout(finish, timeoutMs);
    document.addEventListener('scrollend', onScrollEnd, { capture: true });
  });
}

export function waitForNextFrame(count = 2): Promise<void> {
  return new Promise((resolve) => {
    let remaining = count;
    const tick = () => {
      remaining -= 1;
      if (remaining <= 0) {
        resolve();
        return;
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
}

export function clearTourHighlights(extraClasses: string[] = []): void {
  document.querySelectorAll('.onboarding-highlight').forEach((node) => {
    node.classList.remove('onboarding-highlight', ...extraClasses);
  });
}

export function ensureTargetInViewport(element: HTMLElement, pad = 12): void {
  const rect = element.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const out = rect.top < pad || rect.left < pad || rect.bottom > vh - pad || rect.right > vw - pad;
  if (out) {
    element.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }
}

export function isElementInViewport(element: HTMLElement, pad = 16): boolean {
  const rect = element.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  return rect.top >= pad && rect.left >= pad && rect.bottom <= vh - pad && rect.right <= vw - pad;
}

function readTooltipSize(tooltipSelector = '.guided-tour-tooltip'): { width: number; height: number } {
  const tooltipElement = document.querySelector(tooltipSelector) as HTMLElement | null;
  let width = 420;
  let height = 280;
  if (tooltipElement) {
    const rect = tooltipElement.getBoundingClientRect();
    width = rect.width || width;
    height = rect.height || height;
  }
  const margin = 40;
  width = Math.min(width, window.innerWidth - margin);
  height = Math.min(height, window.innerHeight - margin);
  return { width, height };
}

export function centerTourTooltip(tooltipSelector = '.guided-tour-tooltip'): { x: number; y: number } {
  const { width, height } = readTooltipSize(tooltipSelector);
  const margin = 20;
  let centerX = window.innerWidth / 2;
  let centerY = window.innerHeight / 2;

  const minX = width / 2 + margin;
  const maxX = window.innerWidth - width / 2 - margin;
  const minY = height / 2 + margin;
  const maxY = window.innerHeight - height / 2 - margin;

  centerX = Math.max(minX, Math.min(maxX, centerX));
  centerY = Math.max(minY, Math.min(maxY, centerY));

  document.documentElement.style.setProperty('--tooltip-x', `${centerX}px`);
  document.documentElement.style.setProperty('--tooltip-y', `${centerY}px`);

  return { x: centerX, y: centerY };
}

export function anchorTourTooltip(
  element: HTMLElement,
  step: Pick<GuidedTourStep, 'position' | 'offset'>,
  isLastStep = false
): { x: number; y: number; position: GuidedTourPosition } {
  const rect = element.getBoundingClientRect();
  const position = step.position ?? 'right';
  let x = rect.left + rect.width / 2;
  let y = rect.top + rect.height / 2;

  switch (position) {
    case 'top':
      y = rect.top - 30;
      break;
    case 'bottom':
      y = rect.bottom + 30;
      break;
    case 'left':
      x = rect.left - 30;
      break;
    case 'right':
      x = rect.right + 30;
      break;
    default:
      break;
  }

  if (step.offset) {
    x += step.offset.x;
    y += step.offset.y;
  }

  const isMobile = window.innerWidth <= 1024;
  const { width: tooltipWidth, height: tooltipHeight } = readTooltipSize();
  const margin = 20;

  if (isMobile) {
    return { ...centerTourTooltip(), position: 'top' };
  }

  if (isLastStep && position === 'right') {
    x = rect.right + tooltipWidth / 2 + 30;
    y = rect.top + rect.height / 2;
    if (step.offset) {
      x += step.offset.x;
      y += step.offset.y;
    }
  }

  const minX = tooltipWidth / 2 + margin;
  const maxX = window.innerWidth - tooltipWidth / 2 - margin;
  const minY = tooltipHeight / 2 + margin;
  const maxY = window.innerHeight - tooltipHeight / 2 - margin;

  x = Math.max(minX, Math.min(maxX, x));
  y = Math.max(minY, Math.min(maxY, y));

  document.documentElement.style.setProperty('--tooltip-x', `${x}px`);
  document.documentElement.style.setProperty('--tooltip-y', `${y}px`);

  return { x, y, position };
}
